import * as React from 'react';
import { Message } from 'ai';
import { ToolCall } from '../types';
import { ToolInvocation } from './tool-invocation';
import { MarkdownRenderer } from './markdown-renderer';
import { cn } from '../../../../components/ui';
import { supernovaColor } from '../types';

interface ChatMessageProps {
  message: Message & { parts?: Array<any> };
  onRunSqlQuery?: (query: string) => void;
  className?: string;
}

/**
 * Chat Message Component
 * 
 * Renders a chat message with support for markdown and tool invocations.
 */
export const ChatMessage = React.memo(function ChatMessage({ message, onRunSqlQuery, className = '' }: ChatMessageProps) {
  // Skip system messages
  if (message.role === 'system') {
    return null;
  }

  // Extract parts from the message
  const parts = message.parts || [];
  
  // Determine message styling based on role
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  
  // Use theme-aware classes instead of hardcoded styles
  const messageClasses = cn(
    "flex flex-col rounded-lg p-4",
    isUser && "ml-auto max-w-[75%] bg-primary/10 border border-primary/20 text-primary-foreground",
    isAssistant && "mr-auto w-full max-w-none overflow-visible bg-card border border-border text-card-foreground",
    className
  );
  
  // If there are no parts, render the message content directly with markdown
  if (parts.length === 0) {
    return (
      <div className={messageClasses}>
        <MarkdownRenderer 
          content={message.content} 
          className={isUser ? 'user-message' : 'assistant-message'}
        />
      </div>
    );
  }
  
  // Create an array to hold all content in order
  const orderedContent = [];
  
  // Process each part
  for (const part of parts) {
    // Handle text parts with markdown
    if (part.type === 'text') {
      orderedContent.push(
        <div key={`text-${orderedContent.length}`}>
          <MarkdownRenderer 
            content={part.text} 
            className={isUser ? 'user-message' : 'assistant-message'}
          />
        </div>
      );
    } 
    // Handle tool invocation parts
    else if (part.type === 'tool-invocation') {
      const toolInvocation = part.toolInvocation;
      
      // Convert to the format expected by ToolInvocation component
      const toolCall: ToolCall = {
        id: toolInvocation.id || `tool-${orderedContent.length}`,
        name: toolInvocation.toolName,
        parameters: toolInvocation.args || {},
        result: toolInvocation.result,
        state: toolInvocation.state
      };
      
      // Add to ordered content
      orderedContent.push(
        <div key={`tool-${orderedContent.length}`} className="mt-2 mb-1">
          <ToolInvocation 
            toolCall={toolCall} 
            onRunSqlQuery={onRunSqlQuery}
          />
        </div>
      );
    }
  }
  
  // Return all content in order
  return (
    <div className={messageClasses}>
      {orderedContent}
    </div>
  );
}); 